import clsx from "clsx";
import { useLocale, useTranslations } from "next-intl";
import React from 'react';



import {
	getOrganisationDisplayName,
	newlineToBreak,
	sanitizeMarkup
} from "@/lib/util";

import ZorgbaseResult, { translateZorgbaseResult } from "@/app/api/filter/[postcode]/ZorgbaseResult";

import styles from "./OrganisationResult.module.scss";


export interface OrganisationResultProps {
	result: ZorgbaseResult;
	onMouseOver?: (e: React.MouseEvent<HTMLButtonElement>) => void;
	onMouseOut?: (e: React.MouseEvent<HTMLButtonElement>) => void;
}

export default function OrganisationResult({
	result,
	onMouseOver,
	onMouseOut
}: OrganisationResultProps) {
	const locale = useLocale();
	const t = useTranslations();
	let item = translateZorgbaseResult(result, locale);

	return (
		<div className={styles.OrganisationResult} data-id={item.id}>
			{item.lastUpdated && (
				<small>
					{t("MapDisplay.LastUpdated")}: {item.lastUpdated}
				</small>
			)}
			<h3>{getOrganisationDisplayName(item)}</h3>

			<div className={clsx('flex-row', styles.legend)}>
				{!item.acceptsPatients ||
					<div className={clsx('col-6', styles.registration)}>
						{/* eslint-disable-next-line @next/next/no-img-element */}
						<img src="/icons/patient.svg" alt={`icon ${t('MapDisplay.OpenToPatients')}`} />
						<div>{t('MapDisplay.OpenToPatients')}</div>
					</div>
				}
				{!item.acceptsPassersBy ||
					<div className={clsx('col-6', styles.passersBy)}>
						{/* eslint-disable-next-line @next/next/no-img-element */}
						<img src="/icons/passant.svg" alt={`icon ${t('MapDisplay.OpenToPassersBy')}`} />
						<div>{t('MapDisplay.OpenToPassersBy')}</div>
					</div>
				}
			</div>


			<div className={styles.location}>
				<div dangerouslySetInnerHTML={sanitizeMarkup(item.addressString)} />

				<div>
					{/* eslint-disable-next-line @next/next/no-img-element */}
					<img src="/icons/location.svg" alt={`icon`} />
					<small>{item.distance} km</small>
				</div>
			</div>

			<div className={clsx('flex-row', styles.contact)}>
				{item.phoneNumber && (item.phoneNumber.substring(0, 2) !== '06') && (
					<div className="col-6">
						<a
							href={`tel:${item.phoneNumber}`}
							className="text-decoration-none"
						>
							{/* eslint-disable-next-line @next/next/no-img-element */}
							<img src="/icons/phone.svg" alt={`icon`} />
							{item.phoneNumber}
						</a>
					</div>
				)}

				{item.url && (
					<div className="col-6">
						<a
							href={item.url}
							className="text-decoration-none"
							target="_blank"
							rel="noreferrer"
						>
							{/* eslint-disable-next-line @next/next/no-img-element */}
							<img src="/icons/arrow-right.svg" alt={`icon`} />
							website
						</a>
					</div>
				)}
			</div>

			{!(item.acceptsPatients && item.acceptsPatientsRestrictions) || (
				<>
					<h5>{t("MapDisplay.ConditionsForPatients")}</h5>
					<p dangerouslySetInnerHTML={sanitizeMarkup(item.acceptsPatientsRestrictions)} />

					{item.acceptsPatientsUrl && (
						<p>
							<a href={item.acceptsPatientsUrl}
								className="button primary"
								target="_blank"
								rel="noreferrer">
								{/* eslint-disable-next-line @next/next/no-img-element */}
								<img src="/icons/register.svg" alt={`icon`} />
								{t("MapDisplay.DirectInschrijven")}
							</a>
						</p>
					)}
				</>
			)
			}

			{
				!(item.acceptsPassersBy && item.acceptsPassersByRestrictions) || (
					<>
						<h5>{t("MapDisplay.ConditionsForPassersBy")}</h5>
						<p dangerouslySetInnerHTML={sanitizeMarkup(item.acceptsPassersByRestrictions)} />
					</>
				)
			}
		</div >
	);
}
